import React, { useCallback, useEffect, useState } from 'react';
import { toast } from '../../utils/toast';
import { useAuth } from '../../context/auth-provider';
import { useDocumentTitle } from '../hooks/use-document-title';
import { LogoMark } from '../logo/logo-mark';
import { AppButton } from '../button/app-button';
import { AccountApiError, getClosure, reopenAccount } from '../../api/account-client';
import { BrandRow, Card, Heading, Shell, Subheading, Wordmark } from '../onboarding/onboarding.styled';
import { formatDate } from '../../function-utils/locale';

/**
 * THE ACCOUNT IS CLOSING (Independent Stays IS8, IS-Q14).
 *
 * The owner of a Stays-only account closed it. It is frozen for the grace
 * period: the server refuses everyone with 403 ACCOUNT_CLOSING, except the
 * OWNER, who may read the closure and reopen it. This screen replaces the whole
 * app (App.tsx), like the suspended screen.
 *
 *  - Owner, inside the freeze: the date it will be removed, and Reopen.
 *  - Anyone else (or after the freeze): what has happened and Sign out.
 *
 * Reopening clears the flag and re-reads the session, so the app comes back
 * where it was.
 */

export const CLOSING_TITLE = 'This account is closing';
export const CLOSING_OWNER_TEXT = (until: string) =>
  `You closed this Lobbero Stays account. It is frozen until ${until}: nobody can sign in, and your guests' check-in links do not work. Reopen it before then and everything comes back. After that date the account and its data are removed for good.`;
export const CLOSING_OTHER_TEXT =
  "The owner of this Lobbero Stays account has closed it, so nobody can sign in. Only the account's owner can reopen it.";

// The product locale's formatter (the date rule: no screen formats a date by itself).
const day = (iso: string) => formatDate(iso, { day: 'numeric', month: 'long', year: 'numeric' });

export const AccountClosing: React.FC = () => {
  const { logout, refreshAuth, setAccountClosing } = useAuth();
  const [until, setUntil] = useState<string | null>(null);
  const [canReopen, setCanReopen] = useState(false);
  const [busy, setBusy] = useState(false);
  useDocumentTitle('Account closing');

  const read = useCallback(async () => {
    try {
      const c = await getClosure();
      if (!c.closing) {
        // Open again (reopened from another tab): back to the app.
        setAccountClosing(false);
        await refreshAuth();

        return;
      }
      setUntil(c.closure ? c.closure.freeze_until : null);
      setCanReopen(c.can_reopen);
    } catch {
      // Not the owner (ACCOUNT_CLOSING / ACCOUNT_OWNER_ONLY): the plain text.
      setCanReopen(false);
    }
  }, [refreshAuth, setAccountClosing]);

  useEffect(() => {
    read();
  }, [read]);

  const reopen = async () => {
    setBusy(true);
    try {
      const r = await reopenAccount();
      toast.success(
        r.billing_resumed
          ? 'Your account is open again.'
          : 'Your account is open again. Your subscription is still set to end: resume it from Billing.'
      );
      setAccountClosing(false);
      await refreshAuth();
    } catch (e) {
      toast.error(e instanceof AccountApiError ? e.message : 'That did not go through. Please try again.');
      await read();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <Card>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>
        <Heading>{CLOSING_TITLE}</Heading>
        <Subheading>{canReopen && until ? CLOSING_OWNER_TEXT(day(until)) : CLOSING_OTHER_TEXT}</Subheading>
        {canReopen ? (
          <AppButton onClick={reopen} loading={busy}>
            Reopen my account
          </AppButton>
        ) : null}
        <AppButton variant="secondary" onClick={() => logout()}>
          Sign out
        </AppButton>
      </Card>
    </Shell>
  );
};
